/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Between the wall clock an admin types and the instant the platform stores.
 *
 * The event form promises "Times are stored against the timezone you choose,
 * not the browser's" — `event.timezone` is a required IANA zone on the form.
 * These two functions are what make that sentence true.
 *
 * A `datetime` column is created as TIMESTAMP (no zone), and the backend's
 * scheduled-transition sweep compares it with `now()` over a connection whose
 * session timezone is UTC. So the stored value is only ever read correctly if
 * it holds UTC. An admin in Asia/Kolkata entering "18:00" means 12:30 UTC, and
 * that is what must land — otherwise registration closes 5½ hours late.
 *
 * Neither direction uses the BROWSER's zone. The event's own zone is the whole
 * point: an admin in Delhi scheduling a London event types London time.
 *
 * Both functions pass the value through UNCHANGED when there is no usable
 * timezone, so every caller that has no zone to offer behaves exactly as it did
 * before this module existed.
 */

/** `YYYY-MM-DDTHH:MM`, the only shape `<input type="datetime-local">` accepts. */
const WALL_CLOCK_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

/** A trailing `Z` or `±HH:MM` — the value already names its own zone. */
const HAS_ZONE_RE = /(Z|[+-]\d{2}:?\d{2})$/i;

/** Whether this engine's ICU actually knows the zone. */
export function isSupportedTimeZone(timeZone) {
  if (!timeZone || typeof timeZone !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    // RangeError for an unknown zone. An event whose timezone this browser
    // cannot resolve is left alone rather than silently shifted by a guess.
    return false;
  }
}

/** The calendar fields this instant shows as, in `timeZone`. */
function partsInZone(instant, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = {};
  formatter.formatToParts(instant).forEach((part) => {
    if (part.type !== 'literal') parts[part.type] = part.value;
  });
  return parts;
}

/**
 * How far `timeZone` sits from UTC at this instant, in milliseconds.
 *
 * Read from the zone's own rules rather than a fixed table, so a zone that
 * observes DST reports the offset in force on that date — the reason a fixed
 * "+05:30" constant would be wrong for most of the world.
 */
function offsetMsAt(instant, timeZone) {
  const parts = partsInZone(instant, timeZone);
  // `h23` should never yield 24, but engines have historically disagreed about
  // midnight and a 24 here would silently land the value on the wrong day.
  const hour = Number(parts.hour) % 24;
  const asIfUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    hour,
    Number(parts.minute),
    Number(parts.second),
  );
  return asIfUtc - instant.getTime();
}

/**
 * Read a stored datetime as the UTC instant it names.
 *
 * A value with no zone designator is treated as UTC, because that is what the
 * column holds. `new Date("2026-09-15T12:30:00")` would instead parse it in the
 * BROWSER's zone — the exact bug this module exists to remove, and the reason
 * the `Z` is appended rather than left to the engine.
 */
function parseStoredUtc(value) {
  const text = String(value).trim().replace(' ', 'T');
  if (!text) return null;
  const instant = new Date(HAS_ZONE_RE.test(text) ? text : `${text}Z`);
  return Number.isNaN(instant.getTime()) ? null : instant;
}

/**
 * The wall clock the admin typed → the UTC instant to store.
 *
 * Returns an ISO string ending in `Z`. The backend normalises that to naive UTC
 * before it reaches the column, so the offset is applied rather than discarded.
 *
 * Passed through unchanged when there is no usable zone, when the value already
 * names its own zone, or when it is not a wall clock at all — an empty field
 * stays empty and a caller with no timezone keeps today's behaviour exactly.
 */
export function wallClockToUtcIso(wallClock, timeZone) {
  if (wallClock === null || wallClock === undefined || wallClock === '') return wallClock;
  if (!isSupportedTimeZone(timeZone)) return wallClock;

  const text = String(wallClock).trim().replace(' ', 'T');
  if (HAS_ZONE_RE.test(text)) return wallClock;

  const match = text.match(WALL_CLOCK_RE);
  if (!match) return wallClock;

  const [, year, month, day, hour, minute] = match.map(Number);
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute);

  // Two passes. The offset depends on the instant, and the instant is what we
  // are solving for, so the first pass uses the wall clock as a stand-in and
  // the second re-reads the offset at the answer. This is what makes the hours
  // either side of a DST change land correctly; without it they are an hour out.
  let instantMs = wallAsUtc - offsetMsAt(new Date(wallAsUtc), timeZone);
  instantMs = wallAsUtc - offsetMsAt(new Date(instantMs), timeZone);

  const instant = new Date(instantMs);
  return Number.isNaN(instant.getTime()) ? wallClock : instant.toISOString();
}

/**
 * The stored UTC instant → the wall clock to put in the input.
 *
 * The exact inverse of `wallClockToUtcIso`, so an event loaded for editing and
 * saved untouched stores the same instant it started with.
 *
 * With no usable zone this falls back to trimming the value textually, which is
 * what this form did before zones were applied — the value renders, unshifted,
 * rather than the field going blank.
 */
export function utcToWallClock(value, timeZone) {
  if (!value) return '';

  if (!isSupportedTimeZone(timeZone)) return trimToWallClock(value);

  // A value that will not parse cannot be converted, and trimming it textually
  // here would put an unconverted UTC wall clock into a field labelled with the
  // event's zone — the very confusion this module removes. An empty field says
  // "no usable value" honestly. Postgres cannot produce one; corrupt input can.
  const instant = parseStoredUtc(value);
  if (!instant) return '';

  const parts = partsInZone(instant, timeZone);
  const hour = String(Number(parts.hour) % 24).padStart(2, '0');
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
}

/**
 * Cut a stored value down to `YYYY-MM-DDTHH:MM` without touching the instant.
 *
 * Postgres hands back seconds (and sometimes an offset), which the input
 * silently rejects — the field would render empty and the admin would "lose"
 * the value. Trimmed textually rather than through `Date`, which would shift it
 * into the browser's zone and quietly change the event's time.
 */
export function trimToWallClock(value) {
  if (!value) return '';
  const text = String(value).replace(' ', 'T');
  const match = text.match(WALL_CLOCK_RE);
  return match ? match[0] : '';
}
